import { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight, MagnifyingGlassMinus, MagnifyingGlassPlus } from "@phosphor-icons/react";

// Self-hosted worker: served from /public so it works identically in dev and production
// builds (webpack's `new URL(..., import.meta.url)` asset resolution is unreliable in
// minified production bundles).
pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;

// Built-in PDF renderer (PDF.js) — no browser plugin, no download/print toolbar.
// Accepts raw PDF bytes (Uint8Array) rather than a URL so rendering never depends
// on blob-URL lifecycle or worker fetch quirks.
export default function PdfViewer({ data, testid = "pdf-viewer" }) {
  const [doc, setDoc] = useState(null);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.25);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let task = null;
    setDoc(null);
    setPage(1);
    setNumPages(0);
    setError(null);
    if (!data) return undefined;
    try {
      // PDF.js keeps a reference to the buffer — give it a copy
      const bytes = data instanceof Uint8Array ? data.slice() : new Uint8Array(data);
      task = pdfjs.getDocument({ data: bytes, isEvalSupported: false });
      task.promise
        .then((d) => {
          if (!cancelled) {
            setDoc(d);
            setNumPages(d.numPages);
          }
        })
        .catch((e) => {
          console.error("PDF.js getDocument failed:", e);
          if (!cancelled) setError("Could not render this PDF in the built-in viewer.");
        });
    } catch (e) {
      console.error("PDF.js init failed:", e);
      setError("Could not render this PDF in the built-in viewer.");
    }
    return () => {
      cancelled = true;
      if (task) task.destroy().catch(() => {});
    };
  }, [data]);

  useEffect(() => {
    if (!doc || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await doc.getPage(page);
        if (cancelled) return;
        const viewport = p.getViewport({ scale });
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await p.render({ canvasContext: ctx, viewport }).promise;
      } catch {
        if (!cancelled) setError("Could not render this page.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doc, page, scale]);

  if (error) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-muted-foreground p-6 text-center" data-testid={`${testid}-error`}>
        {error}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-background">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid={`${testid}-prev`}>
            <CaretLeft size={14} />
          </Button>
          <span className="text-xs text-muted-foreground min-w-[80px] text-center" data-testid={`${testid}-page-indicator`}>
            {numPages ? `Page ${page} of ${numPages}` : "Loading…"}
          </span>
          <Button size="sm" variant="outline" disabled={!numPages || page >= numPages} onClick={() => setPage((p) => Math.min(numPages, p + 1))} data-testid={`${testid}-next`}>
            <CaretRight size={14} />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={() => setScale((s) => Math.max(0.6, +(s - 0.2).toFixed(2)))} data-testid={`${testid}-zoom-out`}>
            <MagnifyingGlassMinus size={14} />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setScale((s) => Math.min(2.5, +(s + 0.2).toFixed(2)))} data-testid={`${testid}-zoom-in`}>
            <MagnifyingGlassPlus size={14} />
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-muted/40 flex justify-center p-3">
        <canvas ref={canvasRef} className="shadow-md bg-white self-start" data-testid={`${testid}-canvas`} />
      </div>
    </div>
  );
}
